import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SeoHead from '@/components/base/SeoHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articleMap, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleMap[slug] : undefined;

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return getRelatedArticles(article.slug, article.category);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-semibold text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，你寻找的文章可能已被移除或地址有误。</p>
            <Link to="/" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://asjym.cn' },
          { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://asjym.cn/category/${article.category}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.date,
        url: `https://asjym.cn/article/${article.slug}`,
        image: article.coverImage,
        publisher: {
          '@type': 'Organization',
          name: '长日光阴',
        },
      },
    ],
  };

  return (
    <>
      <SeoHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://asjym.cn/article/${article.slug}`}
        ogType="article"
        articlePublishedTime={article.date}
        articleAuthor={article.author}
        ogImage={article.coverImage}
      />
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <Navbar />

      <main className="pt-20">
        <div className="bg-background-50">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />
        </div>

        <article className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs text-primary-600 bg-primary-50 px-3 py-1 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap hover:bg-primary-100"
                >
                  {article.categoryName}
                </Link>
                <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span className="text-xs text-foreground-400 whitespace-nowrap">{article.readTime}</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-600 font-medium whitespace-nowrap">{article.author}</span>
              </div>
            </header>

            <figure className="relative aspect-[16/9] rounded-lg overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </figure>

            <div
              className="prose prose-lg max-w-none
                prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-base
                prose-strong:text-foreground-900 prose-strong:font-semibold
                prose-blockquote:border-l-primary-400 prose-blockquote:bg-background-100 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-md prose-blockquote:text-foreground-600 prose-blockquote:italic prose-blockquote:not-italic"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-background-200/70">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 text-xs bg-secondary-50 text-secondary-700 rounded-full whitespace-nowrap"
                >
                  # {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-8 text-center">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden cursor-pointer transition-all duration-300 hover:bg-background-100"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-base font-heading font-semibold text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-2 whitespace-nowrap">{ra.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}